import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  Croissant,
  Coffee,
  Shirt,
  ShoppingBasket,
  Scissors,
  Smartphone,
  Utensils,
  BookOpen,
  Star,
  MapPin,
  Ticket,
  MessageSquare,
} from "lucide-react";
import { RevealLines, FadeUp, Stagger, staggerItem } from "@/components/motion/Reveal";
import DownloadApp from "@/components/DownloadApp";
import { APK_URL } from "@/lib/constants";
import api from "@/lib/api";
import { normalizeCategories, normalizeShop } from "@/lib/adapt";

const HERO_IMG =
  "https://images.pexels.com/photos/35350970/pexels-photo-35350970.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

const iconMap = {
  bakery: Croissant,
  cafe: Coffee,
  clothing: Shirt,
  grocery: ShoppingBasket,
  salon: Scissors,
  electronics: Smartphone,
  restaurant: Utensils,
  books: BookOpen,
};

const chapters = [
  {
    n: "01",
    title: "Discover what's around the corner",
    body: "Open the map and watch your neighbourhood light up. Bakeries, boutiques, barbers — every independent shop within walking distance, ranked by how close they are to you.",
  },
  {
    n: "02",
    title: "Claim offers that actually save you money",
    body: "Real coupons from real shopkeepers. Tap to claim, show the code at the counter, and redeem in seconds. No fine print, no middlemen.",
  },
  {
    n: "03",
    title: "Talk directly to the people behind the counter",
    body: "Message a shop, ask about stock, book a slot. NearbyDeals keeps commerce human — and keeps money circulating in your community.",
  },
];

export default function Landing() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroTextY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get("/categories")
      .then((r) => setCategories(normalizeCategories(r.data?.categories).slice(0, 8)))
      .catch(() => {});
    api
      .get("/shops/nearby", { params: { lat: 10.776, lng: 76.654, radius: 60 } })
      .then((r) => setFeatured((r.data || []).map(normalizeShop).slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div className="relative overflow-hidden">
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative min-h-[100svh] grain flex items-end pb-16 pt-32">
        {/* Background text watermark */}
        <motion.div
          style={{ y: heroTextY }}
          className="pointer-events-none absolute -top-4 left-0 right-0 z-0 select-none"
        >
          <div className="font-display font-extrabold text-[22vw] leading-none tracking-tighter text-primary/[0.06] whitespace-nowrap pl-4">
            NEARBY
          </div>
        </motion.div>

        <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 w-full grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-end">
          <div>
            <FadeUp>
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 backdrop-blur px-4 py-2 text-sm font-semibold text-primary mb-8">
                <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
                Now live in your neighbourhood
              </span>
            </FadeUp>

            <h1 className="font-display font-extrabold tracking-tighter text-foreground text-[13vw] sm:text-7xl lg:text-[5.5rem] leading-[0.92]">
              <RevealLines lines={["Shop the", "neighbourhood"]} />
              <span className="block text-primary">
                <RevealLines lines={["you love."]} delay={0.24} />
              </span>
            </h1>

            <FadeUp delay={0.5} className="mt-8 max-w-md">
              <p className="text-lg text-muted-foreground leading-relaxed">
                NearbyDeals maps the independent shops around you — with live offers, instant
                coupons and direct chat. Discovery, done locally.
              </p>
            </FadeUp>

            <FadeUp delay={0.65}>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/browse"
                  data-testid="hero-browse-btn"
                  className="group inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-7 py-4 hover:bg-primary/90 transition-colors"
                >
                  Explore shops nearby
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/auth?mode=register"
                  data-testid="hero-signup-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-foreground/20 font-semibold px-7 py-4 hover:border-primary hover:text-primary transition-colors"
                >
                  Join free
                </Link>
              </div>
              <a
                href={APK_URL}
                download="nearbydeals.apk"
                data-testid="hero-download-link"
                className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary transition-colors"
              >
                <Smartphone className="w-4 h-4" /> or download the Android app
              </a>
            </FadeUp>
          </div>

          {/* Hero image, clipped arch + parallax */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <div className="relative arch overflow-hidden h-[560px] shadow-[0_40px_80px_-30px_rgba(42,36,30,0.5)]">
              <motion.img
                src={HERO_IMG}
                alt="Artisan bakery storefront at dusk"
                style={{ y: imgY, scale: imgScale }}
                className="w-full h-[120%] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 to-transparent" />
            </div>
            {/* floating stat card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="absolute -left-8 bottom-12 bg-card rounded-2xl border border-border p-5 shadow-xl w-56"
            >
              <div className="flex items-center gap-1 text-primary mb-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-primary" />
                ))}
              </div>
              <p className="font-display font-bold text-2xl">1,200+ shops</p>
              <p className="text-sm text-muted-foreground">loved by locals</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============ MARQUEE ============ */}
      <section className="border-y border-border bg-foreground text-background py-6">
        <Marquee speed={40} gradient={false} autoFill>
          {["Support Local", "Find Offers", "Shop Nearby", "Claim Coupons", "Discover Independent"].map(
            (t, i) => (
              <span key={i} className="flex items-center gap-8 mx-8">
                <span className="font-display font-medium text-3xl sm:text-4xl tracking-tight">
                  {t}
                </span>
                <span className="text-primary text-3xl">✦</span>
              </span>
            )
          )}
        </Marquee>
      </section>

      {/* ============ MANIFESTO / CHAPTERS ============ */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
        <FadeUp>
          <p className="font-display uppercase tracking-[0.25em] text-primary text-sm font-bold mb-4">
            How it works
          </p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl text-balance">
            Three simple steps between you and the shop next door.
          </h2>
        </FadeUp>

        <div className="mt-16 space-y-px">
          {chapters.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.05}>
              <div className="group grid md:grid-cols-[auto_1fr_1.4fr] gap-6 md:gap-12 items-start py-10 border-t border-border">
                <span className="font-display font-extrabold text-5xl md:text-6xl text-primary/25 group-hover:text-primary transition-colors duration-500">
                  {c.n}
                </span>
                <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-tight">
                  {c.title}
                </h3>
                <p className="text-muted-foreground text-lg leading-relaxed md:pt-1">{c.body}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ============ CATEGORY BENTO ============ */}
      <section className="bg-secondary/60 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
            <FadeUp>
              <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
                Browse by <span className="text-primary italic font-serif-editorial">category</span>
              </h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <Link
                to="/browse"
                className="inline-flex items-center gap-2 font-semibold hover:text-primary transition-colors"
              >
                See all shops <ArrowRight className="w-4 h-4" />
              </Link>
            </FadeUp>
          </div>

          <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
            {categories.map((cat, i) => {
              const Icon = cat.icon;
              const tall = i === 0 || i === 5;
              return (
                <motion.div key={cat.id} variants={staggerItem} className={tall ? "row-span-2" : ""}>
                  <Link
                    to={`/browse?category=${cat.id}`}
                    data-testid={`category-${cat.id}`}
                    className={`group relative flex flex-col justify-between overflow-hidden rounded-3xl border border-border bg-card p-6 hover:border-primary/40 transition-colors duration-300 ${
                      tall ? "h-full min-h-[220px]" : "h-[150px]"
                    }`}
                  >
                    <span className="grid place-items-center w-12 h-12 rounded-2xl bg-secondary text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                      <Icon className="w-6 h-6" />
                    </span>
                    <div>
                      <p className="font-display font-bold text-lg tracking-tight">{cat.name}</p>
                      <span className="text-sm text-muted-foreground group-hover:text-primary transition-colors">
                        Explore →
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* ============ FEATURED SHOPS ============ */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-32">
          <FadeUp>
            <p className="font-display uppercase tracking-[0.25em] text-primary text-sm font-bold mb-4">
              Loved nearby
            </p>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight max-w-2xl">
              Places worth the short walk.
            </h2>
          </FadeUp>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {featured.map((s, i) => (
              <FadeUp key={s.id} delay={i * 0.07}>
                <Link
                  to={`/shops/${s.id}`}
                  data-testid={`featured-${s.id}`}
                  className="group block bg-card rounded-3xl border border-border overflow-hidden hover:shadow-[0_24px_60px_-24px_rgba(198,81,47,0.4)] transition-shadow duration-400"
                >
                  <div className="h-52 overflow-hidden">
                    <img
                      src={s.images?.[0]}
                      alt={s.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-1 text-primary text-sm font-bold mb-1">
                      <Star className="w-4 h-4 fill-primary" /> {s.rating}
                    </div>
                    <h3 className="font-display font-bold text-lg tracking-tight">{s.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> {s.distance_km} km away
                    </p>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </section>
      )}

      {/* ============ TESTIMONIALS ============ */}
      <section className="bg-secondary/60 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeUp>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight max-w-2xl mb-16">
              Neighbours, not <span className="text-primary italic font-serif-editorial">algorithms</span>.
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                q: "I found a bakery two streets away I'd walked past for years. Now it's my Saturday ritual.",
                name: "Aarav Mehta",
                role: "Customer, Bengaluru",
                icon: Croissant,
              },
              {
                q: "Coupons bring people in the door, and the chat means I answer questions before they even arrive.",
                name: "Ravi Kumar",
                role: "Owner, Verdant Coffee",
                icon: MessageSquare,
              },
              {
                q: "Redeeming a coupon takes five seconds. I've saved more this month than my whole subscription box.",
                name: "Anita Rao",
                role: "Customer, Koramangala",
                icon: Ticket,
              },
            ].map((t, i) => {
              const Icon = t.icon;
              return (
                <FadeUp key={i} delay={i * 0.08}>
                  <div
                    className={`relative bg-card rounded-3xl border border-border p-8 h-full ${
                      i === 1 ? "md:-translate-y-6" : ""
                    }`}
                  >
                    <span className="grid place-items-center w-11 h-11 rounded-2xl bg-primary/10 text-primary mb-6">
                      <Icon className="w-5 h-5" />
                    </span>
                    <p className="font-serif-editorial text-xl leading-relaxed text-foreground">
                      “{t.q}”
                    </p>
                    <div className="mt-6 pt-6 border-t border-border">
                      <p className="font-bold">{t.name}</p>
                      <p className="text-sm text-muted-foreground">{t.role}</p>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ GET THE APP ============ */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 pt-24">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-secondary border border-border grid lg:grid-cols-[1.2fr_0.8fr] gap-8 items-center">
            <div className="p-8 sm:p-14">
              <p className="font-display uppercase tracking-[0.25em] text-primary text-sm font-bold mb-4">
                On the go
              </p>
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-balance">
                Take the neighbourhood <span className="text-primary italic font-serif-editorial">with you.</span>
              </h2>
              <p className="mt-5 text-lg text-muted-foreground max-w-md leading-relaxed">
                Get the NearbyDeals Android app — offers, coupons and shop chat, right in your pocket.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <DownloadApp variant="primary" testid="getapp-download-btn" />
                <span className="text-sm text-muted-foreground">Free · Android APK</span>
              </div>
            </div>
            <div className="relative hidden lg:flex items-center justify-center h-full min-h-[280px] pr-10">
              <div className="relative w-[190px] h-[380px] rounded-[2.2rem] bg-foreground p-2.5 shadow-[0_30px_60px_-25px_rgba(42,36,30,0.6)] rotate-3">
                <div className="w-full h-full rounded-[1.7rem] overflow-hidden bg-background grid place-items-center">
                  <div className="text-center px-4">
                    <span className="grid place-items-center w-16 h-16 rounded-3xl bg-primary text-primary-foreground arch mx-auto mb-4">
                      <Smartphone className="w-8 h-8" />
                    </span>
                    <p className="font-display font-extrabold text-xl tracking-tight">NearbyDeals</p>
                    <p className="text-sm text-muted-foreground mt-1">Shop the neighbourhood</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* ============ CTA ============ */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-foreground text-background px-8 sm:px-16 py-20 text-center grain">
            <h2 className="font-display font-extrabold text-4xl sm:text-6xl tracking-tighter max-w-3xl mx-auto leading-[0.95]">
              Your neighbourhood is open. <span className="text-primary">Go say hello.</span>
            </h2>
            <p className="mt-6 text-background/70 text-lg max-w-xl mx-auto">
              Join thousands of locals discovering offers and supporting the shops that make their
              streets feel like home.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link
                to="/auth?mode=register"
                data-testid="cta-signup-btn"
                className="rounded-full bg-primary text-primary-foreground font-semibold px-8 py-4 hover:bg-primary/90 transition-colors"
              >
                Create free account
              </Link>
              <Link
                to="/browse"
                className="rounded-full border border-background/30 font-semibold px-8 py-4 hover:bg-background/10 transition-colors"
              >
                Browse first
              </Link>
            </div>
          </div>
        </FadeUp>
      </section>
    </div>
  );
}
